import React, { useMemo } from 'react';
import ReactApexChart from 'react-apexcharts';
import { BarChart3 } from 'lucide-react';

const INSIGHT_COLORS = ['#1a767e', '#4263eb', '#7950f2', '#f59f00', '#e8590c', '#2f9e44'];
// The two roll-up slices are deliberately neutral grey: they are residual buckets, not
// categories competing for attention with the real standards/programs beside them.
const NEUTRAL_INSIGHT_COLORS = { Other: '#868e96', 'Not specified': '#adb5bd' };
const sliceColor = (label, index) => NEUTRAL_INSIGHT_COLORS[label] || INSIGHT_COLORS[index % INSIGHT_COLORS.length];

const AUDIT_CYCLE_PATTERN = /^(initial(\s+audit|\s+certification)?|surveillance(\s+\d+(st|nd|rd|th))?|recertification|stage\s*[12])$/i;
const SERVICE_PROGRAM_SIGNAL_PATTERN = /\b(initial(\s+audit|\s+certification)?|surveillance|re-?certification|certification|transfer\s+re-?certification|stage\s*[12])\b/i;

/** Slice shown for projects whose standard/program could not be resolved at all. */
export const UNSPECIFIED_INSIGHT_LABEL = 'Not specified';
/** Slice that carries every category past the display limit, so nothing is silently dropped. */
export const OTHER_INSIGHT_LABEL = 'Other';
/** Named slices a donut renders before the remainder is rolled into "Other". */
export const INSIGHT_SLICE_LIMIT = 6;

/**
 * A task's real audit standard, or null when it has none.
 *
 * This used to additionally require the value to match a hardcoded keyword whitelist
 * (ISO|IEC|ISCC|EU|JAPAN|FIT|...), which silently rejected genuine standards outside that list --
 * CORSIA and PLUS both exist in the data and always rendered as "-". Only the cycle-label guard
 * remains: the backend's task fallback can put a cycle name ("Initial Certification") in this
 * field, and that is not a standard.
 */
export const resolveStandardInsightLabel = (task) => {
  const candidate = String(task?.standardName || '').trim();
  if (!candidate || SERVICE_PROGRAM_SIGNAL_PATTERN.test(candidate)) return null;
  return candidate;
};

export const resolveServiceProgramInsightLabel = (task) => {
  const standardValue = String(task?.standardName || '').trim();
  if (AUDIT_CYCLE_PATTERN.test(standardValue) || SERVICE_PROGRAM_SIGNAL_PATTERN.test(standardValue)) return standardValue;
  return task?.serviceName || task?.serviceCategory || task?.programCategory || task?.auditCycle || null;
};

export const countInsightValues = (tasks, getter) => Object.entries((Array.isArray(tasks) ? tasks : []).reduce((result, task) => {
  const key = getter(task) || 'Not specified';
  result[key] = (result[key] || 0) + 1;
  return result;
}, {})).sort(([, a], [, b]) => b - a);

/**
 * Groups tasks into donut slices whose counts ALWAYS add up to `tasks.length`.
 *
 * The previous countInsightValues().slice(0, 6) discarded every category past the sixth without
 * a trace, so the donut's centre total read lower than the table's project count with no
 * explanation (517 projects vs "510" and "244"). Categories past the limit are now rolled into
 * one "Other" slice, and unresolved rows -- previously filtered out of the standards donut
 * entirely -- appear as an explicit "Not specified" slice. Both are pinned last.
 *
 * Each slice carries the raw labels it stands for, so clicking it can filter the table to every
 * row it represents, not just to a label that no row literally has.
 */
export const buildInsightSlices = (tasks, getter, limit = INSIGHT_SLICE_LIMIT) => {
  const tallied = countInsightValues(tasks, getter);
  const named = tallied.filter(([label]) => label !== UNSPECIFIED_INSIGHT_LABEL);
  const unspecifiedCount = tallied.find(([label]) => label === UNSPECIFIED_INSIGHT_LABEL)?.[1] || 0;

  const slices = named.slice(0, limit).map(([label, count]) => ({ label, count, values: [label] }));

  const overflow = named.slice(limit);
  if (overflow.length > 0) {
    slices.push({
      label: OTHER_INSIGHT_LABEL,
      count: overflow.reduce((sum, [, count]) => sum + count, 0),
      values: overflow.map(([label]) => label),
    });
  }
  if (unspecifiedCount > 0) {
    slices.push({
      label: UNSPECIFIED_INSIGHT_LABEL,
      count: unspecifiedCount,
      values: [UNSPECIFIED_INSIGHT_LABEL],
    });
  }
  return slices;
};

export const matchesMonitoringInsight = (row, filter) => {
  if (!filter) return true;
  const label = (filter.type === 'standard' ? resolveStandardInsightLabel(row) : resolveServiceProgramInsightLabel(row))
    || UNSPECIFIED_INSIGHT_LABEL;
  // Rolled-up slices ("Other", "Not specified") stand for several raw labels at once.
  return Array.isArray(filter.values) ? filter.values.includes(label) : label === filter.value;
};

const MonitoringInsights = ({ tasks = [], title = 'Monitoring Insights', activeFilter = null, onFilterChange, embedded = false }) => {
  // Every project lands in exactly one slice of each donut, so both centre totals equal the
  // project count shown in the section header, the stage bar, and the table footer.
  const standards = useMemo(() => buildInsightSlices(tasks, resolveStandardInsightLabel), [tasks]);
  const services = useMemo(() => buildInsightSlices(tasks, resolveServiceProgramInsightLabel), [tasks]);
  const pie = (items, type) => items.length === 0 ? <div className="text-muted small py-5 text-center">No data available.</div> : <ReactApexChart type="donut" height={250} series={items.map((slice) => slice.count)} options={{ chart: { type: 'donut', events: { dataPointSelection: (_event, _context, config) => { const item = items[config.dataPointIndex]; if (!item) return; const next = { type, value: item.label, values: item.values }; onFilterChange?.(activeFilter?.type === type && activeFilter?.value === item.label ? null : next); } } }, labels: items.map((slice) => slice.label), colors: items.map((slice, index) => activeFilter && !(activeFilter.type === type && activeFilter.value === slice.label) ? `${sliceColor(slice.label, index)}55` : sliceColor(slice.label, index)), stroke: { width: 3, colors: ['#fff'] }, dataLabels: { enabled: true, formatter: (_value, options) => `${options.w.config.series[options.seriesIndex]}`, style: { fontSize: '11px', fontWeight: 700 }, dropShadow: { enabled: false } }, legend: { position: 'bottom', fontSize: '10px', markers: { width: 8, height: 8 }, itemMargin: { horizontal: 6, vertical: 3 } }, plotOptions: { pie: { donut: { size: '62%', labels: { show: true, total: { show: true, label: 'Projects', fontSize: '11px', formatter: (chart) => chart.globals.seriesTotals.reduce((sum, value) => sum + value, 0) } } } } }, tooltip: { y: { formatter: (value) => `${value} project${value === 1 ? '' : 's'}` } } }} />;
  const heading = <div className="d-flex align-items-center gap-2 mb-2"><div className="rounded-2 d-flex align-items-center justify-content-center" style={{ width: 32, height: 32, background: 'rgba(66,99,235,.12)' }}><BarChart3 size={16} color="#4263eb" /></div><div className="me-auto"><h3 className="card-title fw-semibold mb-0">{title}</h3><div className="text-muted small">Click a slice to filter the monitoring table below.</div></div>{activeFilter && <button type="button" className="btn btn-sm btn-link" onClick={() => onFilterChange?.(null)}>Clear filter</button>}</div>;
  const content = <>{!embedded && heading}{embedded && activeFilter && <div className="d-flex justify-content-end mb-2"><button type="button" className="btn btn-sm btn-link" onClick={() => onFilterChange?.(null)}>Clear insight filter</button></div>}<div className="row g-3"><div className="col-12 col-lg-6"><div className="rounded-3 border h-100 px-2 pt-3"><div className="text-uppercase text-muted fw-semibold small px-2" style={{ letterSpacing: '.06em' }}>By standard</div>{pie(standards, 'standard')}</div></div><div className="col-12 col-lg-6"><div className="rounded-3 border h-100 px-2 pt-3"><div className="text-uppercase text-muted fw-semibold small px-2" style={{ letterSpacing: '.06em' }}>By service / program</div>{pie(services, 'program')}</div></div></div></>;
  return embedded ? <section className="mb-4">{content}</section> : <section className="card border-0 shadow-sm mb-4"><div className="card-body">{content}</div></section>;
};

export default MonitoringInsights;
